import { buildingLevels } from './building.js';
import { withStairContext } from './stairs.js';
import { sceneWalls } from './siteGeometry.js';
import { siteSurfaces } from './siteAreas.js';
import { recordingFor } from './recording.js';
import { camGeom } from './optics.js';
import { structuralSlabs, polygonPrism, fitCeilingToFloors, SLAB_DEPTH } from './buildingEnvelope.js';
export { SLAB_DEPTH } from './buildingEnvelope.js';

const cache = new WeakMap();

/** Heights in the document are relative to their owning floor. Sight lines
 * need a shared physical scene, including levels hidden by the cutaway UI. */
export function buildingVisibility(S) {
  const levels = buildingLevels(S).map(f => ({ ...f, plan: { ...withStairContext(S, f), activeFloorId: f.id, elevation: f.elevation, floorName: f.name } }));
  const key = JSON.stringify(levels.map(f => [f.id, f.elevation, f.plan.areas, f.plan.walls, f.plan.openings, f.plan.obstacles, f.plan.sceneState, f.plan.stairs, f.plan._stairLevels]));
  const previous = cache.get(S);
  if (previous?.key === key) return { ...previous.value, levels };
  // Retain a flat fallback below the lowest modeled level. It must never be
  // an imaginary slab across the sky at an upper camera's owning elevation.
  const datum = levels.find(f => f.id === 'ground')?.elevation ?? 0;
  const bottom = Math.min(datum, ...levels.map(f => f.elevation));
  const floors = levels.flatMap(f => [
    ...structuralSlabs(f.plan).map(s => ({ ...s, floorId: f.id, elevation: f.elevation })),
    ...siteSurfaces({ ...f.plan, areas: f.plan.areas.filter(a => a.kind !== 'exclude') }).layers.filter(l => l.area.environment === 'outdoor').flatMap(l => l.polygons.map(rings => ({ rings, area: l.area, floorId: f.id, elevation: f.elevation })))
  ]);
  const walls = [];
  const stairParts = new Set();
  for (const level of levels) {
    for (const w of sceneWalls({ ...level.plan, _sceneWalls: undefined })) {
      if (w.objectKind === 'stair') {
        if (stairParts.has(w.id)) continue;
        stairParts.add(w.id);
      }
      const elevated = { ...w, id: JSON.stringify([level.id, w.id]), sourceFloorId: level.id,
        bottom: (w.bottom || 0) + level.elevation, h: w.h + level.elevation };
      if (!w.roofPts) { walls.push(elevated); continue; }
      const above = floors.filter(f => f.elevation > level.elevation && f.area.environment !== 'outdoor')
        .map(f => ({ elevation: f.elevation, polygons: [f.rings] }));
      walls.push(...fitCeilingToFloors(elevated, above));
    }
  }
  floors.forEach((f, i) => walls.push(polygonPrism(f.rings, { id: 'slab:' + i, sourceId: f.area.id, sources: f.sources, sourceFloorId: f.floorId,
    name: (f.area.name || 'Floor') + ' slab', objectKind: 'slab', kind: 'solid', mountRule: 'prohibited', thickness: 0,
    bottom: f.elevation - SLAB_DEPTH, h: f.elevation })));
  const value = { walls, bottom };
  cache.set(S, { key, value });
  return { ...value, levels };
}

export function visibilityAtLevel(context, elevation) {
  return { floorHeight: context.bottom - elevation,
    walls: context.walls.map(w => ({ ...w, bottom: w.bottom - elevation, h: w.h - elevation })) };
}

export function withBuildingVisibility(S) {
  return S._sceneWalls || !S.floors?.length ? S : { ...S, _sceneWalls: visibilityAtLevel(buildingVisibility(S), S.elevation || 0).walls };
}

/** A receiving level keeps its sample areas, requirements and target heights.
 * Cameras retain their own saved optics and are translated into that frame.
 * Namespaced IDs prevent duplicated floor snapshots from sharing device data. */
export function buildingCoveragePlan(S, level, context = buildingVisibility(S)) {
  const plan = context.levels.find(f => f.id === level.id)?.plan || level.plan;
  const cams = [], models = [];
  for (const f of context.levels) {
    const modelId = id => JSON.stringify([f.id, id]);
    models.push(...f.plan.models.map(m => ({ ...m, id: modelId(m.id) })));
    cams.push(...f.plan.cams.map(c => ({ ...c, id: JSON.stringify([f.id, c.id]), modelId: modelId(c.modelId),
      recording: { ...recordingFor(f.plan, c), resolutionScale: camGeom(f.plan, c)?.resolutionScale ?? 1 }, sourceCameraId: c.id, sourceFloorId: f.id,
      name: `${c.name} · ${f.name}`, h: c.h + f.elevation - level.elevation })));
  }
  return { ...plan, cams, models, _sceneWalls: visibilityAtLevel(context, level.elevation).walls };
}
